// Painel do save na nuvem.
//
// Mostra o que esta la, o que esta aqui, e deixa o usuario decidir quando os
// dois discordam. Resolver conflito sozinho seria apagar a carreira de alguem
// -- e a pessoa so descobriria depois.

import React, { useEffect, useState } from 'react';
import { pushNow, pullNow, cloudVersion, cloudLastAt, cloudEnabled, adoptVersion, onCloudChange } from './cloud.js';
import { serializeCareer } from '../engine/career.js';
import { getLeagueConfig } from '../data/leagues.js';
import { PREMIUM } from './premium-config.js';

const quando = (iso) => {
  if (!iso) return 'nunca';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const kb = (n) => (n ? `${Math.round(n / 1024)} KB` : '-');

/** Resumo de uma carreira, para comparar a daqui com a de la. */
function Resumo({ save, titulo, destaque }) {
  if (!save) return <div className="card tight dim small">{titulo}: vazio</div>;
  const time = save.teams?.find((t) => t.id === save.clubId);
  const row = save.standings?.[save.clubId];
  const cfg = getLeagueConfig(save.leagueId);
  return (
    <div className={`card tight ${destaque ? 'me' : ''}`} style={destaque ? { borderColor: 'var(--accent)' } : undefined}>
      <h3 style={{ margin: 0 }}>{titulo}</h3>
      <div style={{ fontWeight: 700 }}>{time?.name || '-'}</div>
      <div className="small dim">
        {cfg.name} · temporada {save.season} ·{' '}
        {save.phase === 'regular' ? `rodada ${save.round}` : save.phase}
        {row ? ` · ${row.wins}-${row.losses}` : ''}
      </div>
      <div className="small dim">
        {(save.history?.length || 0)} temporada(s) no historico
      </div>
    </div>
  );
}

export function CloudPanel({ state, onAdopt, premium }) {
  const [estado, setEstado] = useState({ estado: 'parado' });
  const [conflito, setConflito] = useState(null);
  const [remoto, setRemoto] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => onCloudChange(setEstado), []);

  if (!premium) {
    return (
      <div className="card">
        <h3>Save na nuvem</h3>
        <div className="muted small">
          Faz parte do {PREMIUM.name}. Com ele, a carreira acompanha voce entre o
          computador e o celular -- e uma reinstalacao nao apaga nada.
        </div>
      </div>
    );
  }
  if (!cloudEnabled()) {
    return (
      <div className="card">
        <h3>Save na nuvem</h3>
        <div className="muted small">Entre na sua conta para ligar a nuvem.</div>
      </div>
    );
  }

  const enviar = async (force = false) => {
    setBusy(true); setConflito(null);
    const r = await pushNow(JSON.parse(serializeCareer(state)), { force });
    if (r.conflict) setConflito(r.remoto);
    setBusy(false);
  };

  const baixar = async () => {
    setBusy(true); setConflito(null);
    const r = await pullNow();
    if (r.ok) setRemoto(r);
    setBusy(false);
  };

  const aceitarRemoto = (payload) => {
    adoptVersion(payload.version, payload.at);
    onAdopt(payload.save);
    setConflito(null); setRemoto(null);
  };

  return (
    <>
      <div className="card">
        <div className="row spread" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Save na nuvem</h3>
          <span className={`pill ${estado.estado === 'ok' ? 'on' : ''}`}>
            {estado.estado === 'enviando' && 'enviando...'}
            {estado.estado === 'baixando' && 'baixando...'}
            {estado.estado === 'ok' && 'em dia'}
            {estado.estado === 'erro' && 'falhou'}
            {estado.estado === 'conflito' && 'conflito'}
            {estado.estado === 'parado' && (cloudLastAt() ? 'pronto' : 'nunca enviado')}
          </span>
        </div>
        <div className="grid two small">
          <div className="row spread"><span className="dim">Ultimo envio</span><b>{quando(cloudLastAt())}</b></div>
          <div className="row spread"><span className="dim">Versao</span><b>{cloudVersion()}</b></div>
        </div>
        {estado.error && <div className="small neg" style={{ marginTop: 8 }}>{estado.error}</div>}
        <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
          <button className="primary" style={{ flex: 1 }} disabled={busy} onClick={() => enviar(false)}>
            enviar para a nuvem
          </button>
          <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={baixar}>
            ver o que esta la
          </button>
        </div>
        <div className="dim small" style={{ marginTop: 8 }}>
          O envio tambem acontece sozinho enquanto voce joga, alguns segundos
          depois de cada mudanca.
        </div>
      </div>

      {conflito && (
        <div className="card" style={{ borderColor: 'var(--warn)' }}>
          <h3 className="warn" style={{ margin: '0 0 6px' }}>A nuvem tem uma carreira mais nova</h3>
          <div className="small muted" style={{ marginBottom: 10 }}>
            Outro aparelho jogou depois deste. Escolha qual das duas fica -- a outra
            se perde, e por isso a decisao e sua.
          </div>
          <div className="grid" style={{ gap: 8, marginBottom: 10 }}>
            <Resumo save={state} titulo="Aqui neste aparelho" destaque />
            <Resumo save={conflito.save} titulo={`Na nuvem (${quando(conflito.at)})`} />
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            <button className="danger" style={{ flex: 1 }} onClick={() => enviar(true)}>
              manter a daqui
            </button>
            <button className="primary" style={{ flex: 1 }} onClick={() => aceitarRemoto(conflito)}>
              usar a da nuvem
            </button>
          </div>
        </div>
      )}

      {remoto && !conflito && (
        <div className="card">
          <h3>O que esta na nuvem</h3>
          {remoto.save ? (
            <>
              <div className="small dim" style={{ marginBottom: 8 }}>
                Enviado em {quando(remoto.at)} · {kb(remoto.bytes)} · versao {remoto.version}
              </div>
              <Resumo save={remoto.save} titulo="Carreira guardada" />
              <button className="ghost" style={{ width: '100%', marginTop: 8 }}
                onClick={() => aceitarRemoto(remoto)}>
                trazer esta carreira para este aparelho
              </button>
            </>
          ) : (
            <div className="dim small">Nao ha nada guardado ainda.</div>
          )}
        </div>
      )}
    </>
  );
}
